import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {IParticularColor} from '@toolify/server/src/services/ColorService/types/IParticularColor'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {ParticularColorType} from '@toolify/server/src/services/ColorService/enum/ParticularColorType'
import {PropertyValueType} from '@toolify/server/src/services/DatabasePropertyService/types/PropertyValueType'
import {Color,converter, formatHex, wcagContrast} from 'culori'
import {IDatabasePropertyResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {useValueTypeStrategy} from '../../databases/strategies/PropertyValueTypeStrategy/composables/useValueTypeStrategy'
import {getReadableTextColor} from '@toolify/client/src/modules/color/functions/getReadableTextColor'
import {ReadableTextBackgroundColorTypes} from '@toolify/client/src/modules/color/constants/ReadableTextBackgroundColorTypes'

interface ICellValue{
  value: unknown;
  valueType: PropertyValueType;
  sourceItemId?: string;
}

export function useParticularColors() {
  const {getThemeVariable} = useThemeVariables()

  function getColorByParticularColor(particularColor: IParticularColor): string {
    if (!particularColor?.colorType) {
      return null
    }
    if (particularColor.colorType === ParticularColorType.Custom) {
      const customParticularColor = particularColor as IParticularColor<ParticularColorType.Custom>
      return customParticularColor.payload
    }
    if (particularColor.colorType === ParticularColorType.Palette) {
      const paletteParticularColor = particularColor as IParticularColor<ParticularColorType.Palette>
      const palette = getThemeVariable.value(ThemeVariableType.PaletteColors)
      return palette[paletteParticularColor.payload % palette.length]
    }
    throw new Error(`Unknown color type ${particularColor.colorType}`)
  }

  function getReadableTextColorByColor(color: string): string {
    if(!color) {
      return null
    }
    return getReadableTextColor(color, ReadableTextBackgroundColorTypes.map(backgroundColorType => getThemeVariable.value(backgroundColorType)))
  }

  function getColorByCellValue(cellValue:ICellValue, databaseProperty?:IDatabasePropertyResponseInstance) {
    const strategy = useValueTypeStrategy(databaseProperty?.valueType || cellValue?.valueType)
    if(strategy?.fullValueToColor && cellValue?.value) {
      const {color} = strategy.fullValueToColor(cellValue.value, databaseProperty)
      return color
    }
    
    return null
  }

  function getFontColorByBackgroundColor(backgroundColor: string): string {
    try {
      const okLchConverter = converter('oklch')
      const darkColorObject = okLchConverter(backgroundColor)
      darkColorObject.c = 0.01
      darkColorObject.l = 0.1
      const darkColorHex = formatHex(darkColorObject)
      const lightColorObject = okLchConverter(backgroundColor)
      lightColorObject.c = 0.01
      lightColorObject.l = 0.9
      const lightColorHex = formatHex(lightColorObject)
      // const darkColorContrast = getContrastBetweenTwoColors(backgroundColor, darkColorObject)
      // const lightColorContrast = getContrastBetweenTwoColors(backgroundColor, lightColorObject)
      return lightColorHex
    } catch (error) {
      return '#000000'
    }
  }

  function getContrastBetweenTwoColors(color1: string|Color, color2: string|Color): number {
    return wcagContrast(color1, color2)
  }
  
  return {
    getColorByParticularColor,
    getReadableTextColorByColor,
    getColorByCellValue,
    getFontColorByBackgroundColor,
  }
}
